import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import SEO from '../components/SEO';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [content, setContent] = useState('');
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const [indexRes, postRes] = await Promise.all([
          fetch('/blog/index.json'),
          fetch(`/blog/${slug}.html`)
        ]);

        if (!postRes.ok) throw new Error('Post not found');
        const postHtml = await postRes.text();

        let foundMeta = { title: slug };
        if (indexRes.ok) {
          const indexData = await indexRes.json();
          const match = (indexData.posts || []).find((p) => p.slug === slug);
          if (match) foundMeta = match;
        }

        if (active) {
          setContent(postHtml);
          setMeta(foundMeta);
          setError(null);
        }
      } catch (err) {
        if (active) setError('Unable to load this post.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [slug]);

  const pageTitle = meta?.title || 'Blog post';
  const pageDesc = meta?.description || 'Read this blog post from Noteician.';

  return (
    <Shell>
      <SEO title={pageTitle} description={pageDesc} canonicalPath={`/blog/${slug}`} />

      <Layout>
        <TopBar>
          <LogoLink to="/">Noteician</LogoLink>
          <TopAction to="/contact">Contact</TopAction>
        </TopBar>

        <Content>
          <BackLink to="/blog">← Back to blog</BackLink>

          {loading && <Status>Loading…</Status>}
          {error && <Status>{error}</Status>}

          {!loading && !error && (
            <ArticleCard>
              <h1>{meta?.title || slug}</h1>
              {meta?.date && (
                <MetaLine>
                  {new Date(meta.date).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </MetaLine>
              )}
              <HtmlBody dangerouslySetInnerHTML={{ __html: content }} />
            </ArticleCard>
          )}
        </Content>
      </Layout>
    </Shell>
  );
};

const Shell = styled.div`
  min-height: 100vh;
  background:
    linear-gradient(180deg, rgba(247, 245, 242, 0.92), rgba(247, 245, 242, 0.95)),
    repeating-linear-gradient(90deg, rgba(0, 0, 0, 0.015) 0, rgba(0, 0, 0, 0.015) 1px, transparent 1px, transparent 18px),
    repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.012) 0, rgba(0, 0, 0, 0.012) 1px, transparent 1px, transparent 16px);
  color: var(--black);
`;

const Layout = styled.div`
  max-width: 940px;
  margin: 0 auto;
  padding: 28px 20px 120px;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 26px;
`;

const LogoLink = styled(Link)`
  font-weight: 800;
  letter-spacing: 0.4px;
  color: var(--black);
  text-decoration: none;
  padding: 9px 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
`;

const TopAction = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: transparent;
  color: var(--black);
  border-radius: 999px;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid rgba(0, 0, 0, 0.1);
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    transform: translateY(-1px);
  }
`;

const Content = styled.div`
  max-width: 840px;
  margin: 0 auto;
  padding: 12px 12px 90px;
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 22px;
  color: var(--primary);
  text-decoration: none;
  font-weight: 600;
`;

const Status = styled.div`
  color: var(--dark-gray);
`;

const ArticleCard = styled.article`
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  padding: 26px 28px 34px;
  box-shadow:
    0 10px 22px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);

  h1 {
    font-size: 2.3rem;
    margin: 4px 0 12px;
    color: var(--black);
    letter-spacing: -0.01em;
    font-family: 'Georgia', 'Times New Roman', serif;
  }
`;

const MetaLine = styled.div`
  color: var(--dark-gray);
  margin-bottom: 24px;
  font-size: 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 12px;
`;

const HtmlBody = styled.div`
  color: var(--black);
  line-height: 1.7;
  font-size: 1.02rem;

  h2, h3, h4 {
    margin-top: 32px;
    margin-bottom: 12px;
    font-family: 'Georgia', 'Times New Roman', serif;
    letter-spacing: -0.01em;
  }

  p {
    margin-bottom: 18px;
    color: var(--dark-gray);
  }

  ul, ol {
    margin-inline-start: 20px;
    margin-bottom: 18px;
    color: var(--dark-gray);
  }

  a {
    color: var(--primary);
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
  }

  blockquote {
    border-inline-start: 3px solid var(--primary);
    padding-inline-start: 14px;
    margin: 20px 0;
    color: var(--dark-gray);
    font-style: italic;
  }

  code {
    background: var(--light-gray);
    padding: 2px 6px;
    border-radius: 4px;
  }

  pre {
    background: var(--light-gray);
    padding: 14px;
    border-radius: 8px;
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 26px 0;
    font-size: 0.8rem;
    line-height: 1.5;
    background: #ffffff;
    border-radius: 14px;
    overflow: hidden;
    box-shadow:
      0 10px 24px rgba(0, 0, 0, 0.04),
      0 1px 0 rgba(255, 255, 255, 0.8);
  }

  th,
  td {
    border: 1px solid rgba(0, 0, 0, 0.06);
    padding: 10px 14px;
    text-align: start;
    vertical-align: top;
  }

  thead th {
    background: rgba(0, 0, 0, 0.03);
    font-weight: 700;
    font-size: 0.72rem;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: var(--dark-gray);
  }

  tbody tr:nth-child(even) {
    background: rgba(0, 0, 0, 0.015);
  }

  table ul {
    margin: 6px 0 0 16px;
  }

  iframe {
    width: 100%;
    max-width: 760px;
    height: 400px;
    border: none;
    border-radius: 12px;
    margin: 12px 0 20px;
  }
`;

export default BlogPostPage;
